import { useId, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { toast } from 'sonner';
import { Images, Type as TypeIcon, Upload } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import { get, post } from '@/lib/api';
import { errMsg, postMultipart, refreshSubmission } from '@/lib/submission-ui';
import {
  printSizeLabel, slotImageUrl, textSlotDescription, textSpecLabel, viewLabel,
  type SlotCard,
} from '@/lib/submission-slots';
import { fontNames, type FontsPayload } from './StoreEditorDrawer';
import { mergeAddtextFonts } from '@/lib/submission-zones';
import { CaptionColorField } from './CaptionColorField';
import { AddtextFontSelect } from './AddtextFontSelect';
import { LogoLibraryPickerDialog } from './LogoLibraryPickerDialog';

// One card per DECORATION SLOT — the unit a back-office worker actually acts
// on. A tier with two logos, a player name and a number renders four of these.
//
// Logo slots carry artwork and get the four artwork actions. Name/number slots
// carry NOTHING to upload (the art is minted per player at order time), so they
// deliberately show no upload/text controls — only what will print there.

export function SlotStatusBadge({ card }: { card: SlotCard }) {
  if (card.status === 'PASS') {
    const templateGap = card.kind !== 'logo' && card.validatedTemplate !== true;
    if (templateGap) {
      return <Badge className="bg-warning/10 text-warning">PASS · template not validated</Badge>;
    }
    return (
      <Badge className="bg-success/10 text-success">
        ✓ PASS{card.designId ? ` (design ${card.designId})` : ''}
      </Badge>
    );
  }
  if (card.status === 'FAIL') return <Badge className="bg-danger/10 text-danger">✗ FAIL</Badge>;
  if (card.status === 'ERROR') return <Badge className="bg-danger/10 text-danger">⚠ ERROR</Badge>;
  return <span className="text-[11px] text-muted">— not run</span>;
}

// The slot's artwork, addressed by SLOT (never by GCS path). A failure here is
// shown, not hidden: the link opens the same URL so the worker reads the
// server's reason instead of staring at a blank square.
function SlotImage({
  src, alt, className,
}: { src: string; alt: string; className?: string }) {
  const [errored, setErrored] = useState(false);
  if (errored) {
    return (
      <a
        href={src}
        target="_blank"
        rel="noreferrer"
        className={cn('flex shrink-0 items-center justify-center rounded-[8px] bg-danger/[0.08] p-1 text-center font-mono text-[8.5px] leading-tight text-danger', className)}
        title="Image failed to load — open for the server's reason"
      >
        LOAD FAILED
      </a>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setErrored(true)}
      className={cn('shrink-0 rounded-[8px] bg-bg object-contain', className)}
      loading="lazy"
    />
  );
}

function DetailList({ label, items, tone }: { label: string; items: string[]; tone: string }) {
  if (!items.length) return null;
  return (
    <div className="mt-1">
      <p className={cn('text-[10px] font-medium uppercase tracking-[0.04em]', tone)}>{label}</p>
      <ul className="ml-3 list-disc text-[10.5px] text-muted">
        {items.map((m, i) => <li key={i}>{m}</li>)}
      </ul>
    </div>
  );
}

type Props = {
  submissionId: string;
  /** 1..MAX_PRIZE_TIERS — T3+ live on `tiers[]`, same route `/preflight/:tier`. */
  tier: number;
  card: SlotCard;
  editable: boolean;
  /** Changes whenever the submission doc changes — busts the image cache. */
  cacheKey: string;
};

export function SlotDecorationCard({ submissionId, tier, card, editable, cacheKey }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [textOpen, setTextOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [text, setText] = useState(card.captionText || '');
  // Picking a file used to upload it on the spot. A replacement logo overwrites
  // the artwork this slot will PRINT — it clears the tier's preflight and, once
  // re-approved, becomes the file the decorator runs. There is no undo on the
  // far side of that, so the file is held here until the worker confirms it.
  const [pending, setPending] = useState<File | null>(null);
  const ids = useId();
  // A slot-targeted add-text has NO server-side defaults: addtext refuses the
  // request unless fontName and textColor are both named (SLOT_INPUT_REQUIRED).
  // These two are the PRE-SELECTED choices, not invented ones — both are real
  // accepted values, both are visible in the panel below, and the worker can
  // change either before applying. 'auto' resolves to the darkest logo color,
  // which is the right answer far more often than a fixed white or black.
  const [font, setFont] = useState(card.captionFont || 'zingrustbase');
  const [textColor, setTextColor] = useState(card.captionTextColor || 'auto');
  const { data: fontsData } = useSWR<FontsPayload>(textOpen ? '/api/fonts' : null, get);
  const fonts = useMemo(() => mergeAddtextFonts(fontNames(fontsData)), [fontsData]);

  const isLogo = card.kind === 'logo';
  const size = printSizeLabel(card);
  const targetLabel = `Tier ${tier} · ${card.label}`;
  const v = encodeURIComponent(cacheKey);

  async function replaceLogo(file: File) {
    setBusy('replace');
    try {
      const fd = new FormData();
      fd.append('slotId', card.slotId);
      fd.append('logo', file);
      await postMultipart(`/api/submissions/${submissionId}/logos/${tier}/replace`, fd);
      toast.success(`${card.label} artwork replaced — re-run preflight for this tier.`);
      setPending(null);
      await refreshSubmission(submissionId);
    } catch (e) {
      toast.error(errMsg(e, 'Replace failed'));
    } finally {
      setBusy(null);
    }
  }

  async function useLibraryLogo(logoId: string) {
    setBusy('library');
    try {
      await post(`/api/submissions/${submissionId}/logos/${tier}/from-library`, { logoId, slotId: card.slotId });
      toast.success(`${card.label} now uses the library logo — re-run preflight for this tier.`);
      setLibraryOpen(false);
      await refreshSubmission(submissionId);
    } catch (e) {
      toast.error(errMsg(e, 'Library copy failed'));
    } finally {
      setBusy(null);
    }
  }

  async function applyText() {
    if (!text.trim()) return;
    // Never send a slot edit the service will refuse — both settings are picker
    // state, so this can only trip if the panel is ever wired to something that
    // can go blank.
    if (!font || !textColor) {
      toast.error(`Pick a ${!font ? 'font' : 'text colour'} before applying — a per-slot edit has no defaults.`);
      return;
    }
    setBusy('text');
    try {
      await post(`/api/submissions/${submissionId}/logos/${tier}/text`, {
        text: text.trim(), fontName: font, textColor, slotId: card.slotId,
      });
      toast.success(`Text added under the ${card.label} logo — re-run preflight for this tier.`);
      setTextOpen(false);
      setText('');
      await refreshSubmission(submissionId);
    } catch (e) {
      toast.error(errMsg(e, 'Add text failed'));
    } finally {
      setBusy(null);
    }
  }

  const hasDetails = card.messages.length + card.errors.length + card.warnings.length > 0 || !!card.error;

  return (
    <div className="rounded-[10px] border border-black/[0.06] bg-bg/40 p-2.5">
      {/* header: what prints here, how big, and where on the garment */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-[12px] font-medium">
            {card.label}
            {card.isPrimary && <span className="ml-1.5 text-[10px] font-normal text-muted">primary</span>}
          </p>
          <p className="text-[10.5px] text-muted">
            <code className="text-[10px]">{card.code}</code>
            {' · '}{viewLabel(card.view)}
            {size ? ` · ${size}` : ''}
            {' · '}<code className="text-[10px] text-subtle">{card.slotId}</code>
          </p>
        </div>
        <SlotStatusBadge card={card} />
      </div>

      {card.unknownLocation && (
        <p className="mt-1 text-[10.5px] text-danger">
          This location code is not in platformConfig/decorationLocations — it cannot be labelled or sized.
        </p>
      )}
      {card.missingPrintArea && !card.unknownLocation && (
        <p className="mt-1 text-[10.5px] text-danger">
          The selected tier {tier} product has no print area for {card.code} — this location cannot print.
        </p>
      )}

      {isLogo ? (
        <>
          {/* Uploaded vs trimmed are usually near-identical at 64px, so they
              read as a duplicate-render bug without captions. The pair is the
              point: a bad trim is visible only by comparing them. */}
          <div className="mt-2 flex items-start gap-2">
            <figure className="m-0 shrink-0">
              <SlotImage
                src={`${slotImageUrl(submissionId, tier, card.index, 'logo')}&v=${v}`}
                alt={`${card.label} uploaded artwork`}
                className="h-16 w-16"
              />
              <figcaption className="mt-0.5 text-center text-[9.5px] uppercase tracking-wide text-muted">
                Uploaded
              </figcaption>
            </figure>
            {card.hasTrimmed && (
              <figure className="m-0 shrink-0">
                <SlotImage
                  src={`${slotImageUrl(submissionId, tier, card.index, 'trimmed')}&v=${v}`}
                  alt={`${card.label} trimmed art`}
                  className="h-16 w-16"
                />
                <figcaption className="mt-0.5 text-center text-[9.5px] uppercase tracking-wide text-muted">
                  Trimmed
                </figcaption>
              </figure>
            )}
            <div className="min-w-0 flex-1 text-[10.5px] text-muted">
              {card.captionText && (
                <p>
                  under text {card.captionText}
                  {card.captionFont ? ` · ${card.captionFont}` : ''}
                  {card.captionTextColor ? ` · ${card.captionTextColor}` : ''}
                </p>
              )}
              {card.dimensions && <p>Fits at {card.dimensions}</p>}
              {card.preflightRevision !== null && <p>Preflight run #{card.preflightRevision}</p>}
              {card.hasHeatmap && (
                <a
                  className="text-primary underline-offset-4 hover:underline"
                  href={`${slotImageUrl(submissionId, tier, card.index, 'heatmap')}&v=${v}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  DPI heatmap
                </a>
              )}
            </div>
          </div>

          {editable && (
            <div className="mt-2 flex flex-wrap gap-1.5 border-t border-divider pt-2">
              <input
                ref={fileRef} type="file" accept="image/*" className="hidden"
                aria-label={`Choose replacement artwork for ${card.label}`}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) setPending(f); e.target.value = ''; }}
              />
              <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => fileRef.current?.click()}>
                <Upload className="h-3.5 w-3.5" aria-hidden /> {busy === 'replace' ? 'Uploading…' : 'Replace logo'}
              </Button>
              <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => setLibraryOpen(true)}>
                <Images className="h-3.5 w-3.5" aria-hidden /> {busy === 'library' ? 'Applying…' : 'From library'}
              </Button>
              <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => setTextOpen((o) => !o)}>
                <TypeIcon className="h-3.5 w-3.5" aria-hidden /> Add text
              </Button>
            </div>
          )}

          {textOpen && (
            <div className="mt-2 space-y-2 rounded-[10px] bg-surface p-2.5">
              {/* Every control names itself. The text field used to rely on its
                  placeholder, which is not an accessible name and vanishes as
                  soon as anything is typed. */}
              <label className="block" htmlFor={`${ids}-text`}>
                <span className="mb-0.5 block text-[10px] uppercase tracking-[0.04em] text-muted">
                  Caption text
                </span>
                <Input
                  id={`${ids}-text`}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  maxLength={100}
                  placeholder={`Text under the ${card.label} logo`}
                />
              </label>
              <div className="flex items-end gap-2">
                <label className="min-w-0 flex-1" htmlFor={`${ids}-font`}>
                  <span className="mb-0.5 block text-[10px] uppercase tracking-[0.04em] text-muted">Font</span>
                  <AddtextFontSelect
                    id={`${ids}-font`}
                    value={font}
                    onChange={setFont}
                    fonts={fonts}
                  />
                </label>
                <label>
                  <span className="mb-0.5 block text-[10px] uppercase tracking-[0.04em] text-muted">Text color</span>
                  <CaptionColorField
                    id={`${ids}-color`}
                    aria-label="Caption color"
                    value={textColor}
                    onChange={setTextColor}
                  />
                </label>
                <Button size="sm" disabled={!!busy || !text.trim()} onClick={applyText}>
                  {busy === 'text' ? 'Applying…' : 'Apply'}
                </Button>
              </div>
              <p className="text-[10px] text-subtle">
                Font and color are sent exactly as selected — a per-location edit has no defaults.
              </p>
            </div>
          )}

          <LogoLibraryPickerDialog
            open={libraryOpen}
            onOpenChange={setLibraryOpen}
            target={targetLabel}
            busy={busy === 'library'}
            onConfirm={useLibraryLogo}
          />

          {/* Explicit confirm before a replacement reaches production artwork. */}
          <Dialog
            open={!!pending}
            onOpenChange={(o) => { if (!o && busy !== 'replace') setPending(null); }}
          >
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Replace the {card.label} artwork?</DialogTitle>
                <DialogDescription>
                  <span className="font-medium">{pending?.name}</span> will overwrite the artwork on{' '}
                  <span className="font-medium">{targetLabel}</span>. This clears tier {tier}'s
                  preflight, and once the proof is approved again this file is what gets printed.
                  The artwork it replaces is not kept — there is no undo.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="ghost"
                  disabled={busy === 'replace'}
                  onClick={() => setPending(null)}
                >
                  Keep current artwork
                </Button>
                <Button
                  disabled={busy === 'replace'}
                  onClick={() => { if (pending) void replaceLogo(pending); }}
                >
                  {busy === 'replace' ? 'Uploading…' : 'Replace artwork'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <div className="mt-2 rounded-[8px] border border-dashed border-black/[0.12] bg-surface/60 p-2">
          <p className="text-[11.5px] font-medium">{textSlotDescription(card)}</p>
          {textSpecLabel(card).length > 0 && (
            <p className="mt-0.5 text-[10.5px] text-muted">{textSpecLabel(card).join(' · ')}</p>
          )}
          {card.pairRole && (
            <p className="mt-0.5 text-[10.5px] text-muted">
              Stacked with <span className="font-medium">{card.pairPartner}</span> — both print in the {viewLabel(card.view).toLowerCase()} area.
            </p>
          )}
          <p className="mt-0.5 text-[10px] text-subtle">Nothing to upload here — the art is generated per player when the order is placed.</p>
        </div>
      )}

      {hasDetails && (
        <div className="mt-1.5">
          <button
            type="button"
            className="text-[10.5px] text-primary underline-offset-4 hover:underline"
            onClick={() => setDetailsOpen((o) => !o)}
          >
            {detailsOpen ? 'Hide' : 'Show'} preflight detail
            {card.errors.length ? ` (${card.errors.length} error${card.errors.length === 1 ? '' : 's'})` : ''}
          </button>
          {detailsOpen && (
            <div className="mt-1 rounded-[8px] bg-surface p-2">
              {card.error && <p className="text-[10.5px] text-danger">{card.error}</p>}
              <DetailList label="Errors" items={card.errors} tone="text-danger" />
              <DetailList label="Warnings" items={card.warnings} tone="text-warning" />
              <DetailList label="Messages" items={card.messages} tone="text-muted" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
